// client/src/pages/InvoicePage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Container, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Stack, Divider,
  Alert, CircularProgress, Button, Grid, Paper,
} from '@mui/material';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EftPaymentDetails from '../components/EftPaymentDetails';
import { downloadInvoice } from '../utils/invoice';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { formatCurrency, vatWasAddedOnTop } from '../utils/currency';
import { API_BASE_URL } from '../utils/media';
import { useStore } from '../context/StoreContext';

const InvoicePage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const access = searchParams.get('access');
  const accessQuery = access ? `?access=${encodeURIComponent(access)}` : '';
  const { token } = useContext(AuthContext);
  const { store } = useStore();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/orders/${id}${accessQuery}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || 'Failed to fetch invoice.');
        }
        setOrder(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (token || access) fetchOrder();
    else setLoading(false);
  }, [id, token, access, accessQuery]);

  if (loading) return <Container sx={{ py: 4 }}><CircularProgress size={28} /></Container>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!order) return null;

  const isCollection = order.fulfillment_type === 'collection';
  // Only a VAT vendor may issue a tax invoice or show a VAT line.
  const isVatVendor = Boolean(order.seller?.vat_number);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" className="no-print" sx={{ mb: 2 }}>
        <Button component={Link} to={`/orders/${order.id}${accessQuery}`} startIcon={<ArrowBackIcon />}>Back to Order</Button>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>Print</Button>
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            onClick={() => downloadInvoice({ orderId: order.id, invoiceNumber: order.invoiceNumber, token, access }).catch((err) => setError(err.message))}
          >
            Download PDF
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 } }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" fontWeight={800} color="primary">{order.seller?.company || store.name}</Typography>
            {order.seller?.address && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{order.seller.address}</Typography>
            )}
            {order.seller?.email && <Typography variant="body2" color="text.secondary">{order.seller.email}</Typography>}
            {order.seller?.phone && <Typography variant="body2" color="text.secondary">{order.seller.phone}</Typography>}
            {order.seller?.vat_number && <Typography variant="body2" color="text.secondary">VAT #: {order.seller.vat_number}</Typography>}
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
            <Typography variant="h4" fontWeight={800}>{isVatVendor ? 'TAX INVOICE' : 'INVOICE'}</Typography>
            <Typography variant="body2" color="text.secondary">Invoice #: {order.invoiceNumber}</Typography>
            <Typography variant="body2" color="text.secondary">Order #: {order.id}</Typography>
            <Typography variant="body2" color="text.secondary">Date: {new Date(order.created_at).toLocaleDateString()}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Bill To</Typography>
            <Typography variant="body2">{order.shipping_name}</Typography>
            <Typography variant="body2" color="text.secondary">{order.buyer?.email}</Typography>
            {order.buyer?.client_number && (
              <Typography variant="body2" color="text.secondary">Client #: {order.buyer.client_number}</Typography>
            )}
            {order.shipping_phone && <Typography variant="body2" color="text.secondary">{order.shipping_phone}</Typography>}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {isCollection ? 'Collection Details' : 'Delivery Address'}
            </Typography>
            {isCollection ? (
              <Typography variant="body2" color="text.secondary">Customer collects in person.</Typography>
            ) : (
              <>
                <Typography variant="body2">{order.shipping_address_line1}</Typography>
                {order.shipping_address_line2 && <Typography variant="body2">{order.shipping_address_line2}</Typography>}
                <Typography variant="body2">
                  {order.shipping_city}{order.shipping_province ? `, ${order.shipping_province}` : ''} {order.shipping_postal_code}
                </Typography>
                <Typography variant="body2">{order.shipping_country}</Typography>
              </>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Payment: EFT ({order.payment_status === 'paid' ? 'paid' : 'awaiting payment'})
            </Typography>
          </Grid>
        </Grid>

        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.items.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  {item.name}
                  {item.variant_label && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {item.variant_label}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{item.quantity}</TableCell>
                <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                <TableCell align="right">{formatCurrency(item.price * item.quantity)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Stack spacing={1} sx={{ width: { xs: '100%', sm: 300 } }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography>{formatCurrency(order.subtotal ?? order.total_amount)}</Typography>
            </Stack>
            {Number(order.discount_amount) > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography color="success.main">Discount {order.coupon_code ? `(${order.coupon_code})` : ''}</Typography>
                <Typography color="success.main">-{formatCurrency(order.discount_amount)}</Typography>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Shipping</Typography>
              <Typography>{Number(order.shipping_fee) === 0 ? 'Free' : formatCurrency(order.shipping_fee)}</Typography>
            </Stack>
            {isVatVendor && order.vat_amount != null && vatWasAddedOnTop(order) && (
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">VAT (15%)</Typography>
                <Typography>{formatCurrency(order.vat_amount)}</Typography>
              </Stack>
            )}
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" fontWeight={700}>Total</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(order.total_amount)}</Typography>
            </Stack>
            {isVatVendor && order.vat_amount != null && !vatWasAddedOnTop(order) && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Includes VAT (15%)</Typography>
                <Typography variant="body2" color="text.secondary">{formatCurrency(order.vat_amount)}</Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        {order.payment_status !== 'paid' && order.status !== 'Cancelled' && (
          <Box sx={{ mt: 3 }}>
            <EftPaymentDetails seller={order.seller} reference={order.invoiceNumber} amount={order.total_amount} />
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary">
          Thank you for your order. This invoice was generated automatically and is valid without a signature.
        </Typography>
      </Paper>
    </Container>
  );
};

export default InvoicePage;
